import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api/api-client-context';
import { createPermissionsApi } from './permissions-api';
import { permissionKeys } from './permissions-keys';

// --- Types ---

export interface PermissionsContextValue {
  /** Check if the user has ALL of the given permissions */
  hasAllPermissions: (permissions: string[]) => boolean;
  /** Check if the user has AT LEAST ONE of the given permissions */
  hasAnyPermission: (permissions: string[]) => boolean;
  /** Check if the user has a single permission */
  hasPermission: (permission: string) => boolean;
  /** True while permissions are being loaded */
  isLoading: boolean;
  /** True once the initial permissions fetch has completed at least once since authentication */
  isReady: boolean;
  /** The raw list of permission strings the user holds */
  permissions: string[];
}

export interface PermissionsProviderProps {
  children: ReactNode;
  /**
   * Whether the user is currently authenticated. When false, the provider
   * skips the permissions fetch (query is disabled). Defaults to true
   * for backward compatibility with tests that pass loadPermissions.
   */
  isAuthenticated?: boolean;
  /**
   * Optional override for loading permissions. When omitted, the provider
   * fetches from GET /users/me/permissions via the 'admin-auth' API client.
   * Pass a custom function in tests or Storybook to avoid real API calls.
   */
  loadPermissions?: () => Promise<string[]>;
}

// --- Context ---

const PermissionsContext = createContext<PermissionsContextValue | null>(null);

// --- Internal: Core logic (no API client dependency) ---

type PermissionsProviderCoreProps = {
  children: ReactNode;
  isAuthenticated: boolean;
  loadPermissions: () => Promise<string[]>;
};

function PermissionsProviderCore({
  children,
  isAuthenticated,
  loadPermissions,
}: PermissionsProviderCoreProps): React.JSX.Element {
  const queryClient = useQueryClient();

  const {
    data: permissions = [],
    isFetched,
    isLoading,
  } = useQuery<string[]>({
    enabled: isAuthenticated,
    queryFn: loadPermissions,
    queryKey: permissionKeys.mine(),
  });

  // When the user signs out, remove cached permissions from the QueryClient
  // so that the next sign-in starts with a fresh fetch instead of serving
  // stale data from the previous user within the global staleTime window.
  useEffect(() => {
    if (!isAuthenticated) {
      queryClient.removeQueries({ queryKey: permissionKeys.all });
    }
  }, [isAuthenticated, queryClient]);

  // Derive effective values that respect the authentication state.
  // When isAuthenticated is false (sign-out), useQuery retains cached data;
  // we must treat that as empty to avoid exposing the previous user's permissions.
  const effectivePermissions = isAuthenticated ? permissions : [];
  const effectiveIsLoading = isAuthenticated ? isLoading : false;
  // isReady = user is authenticated AND the query has completed at least once
  const effectiveIsReady = isAuthenticated && isFetched;

  const permissionsSet = useMemo(() => new Set(effectivePermissions), [effectivePermissions]);

  const hasPermission = useCallback((permission: string): boolean => permissionsSet.has(permission), [permissionsSet]);

  const hasAllPermissions = useCallback(
    (perms: string[]): boolean => perms.every((p) => permissionsSet.has(p)),
    [permissionsSet],
  );

  const hasAnyPermission = useCallback(
    (perms: string[]): boolean => perms.some((p) => permissionsSet.has(p)),
    [permissionsSet],
  );

  const value = useMemo<PermissionsContextValue>(
    () => ({
      hasAllPermissions,
      hasAnyPermission,
      hasPermission,
      isLoading: effectiveIsLoading,
      isReady: effectiveIsReady,
      permissions: effectivePermissions,
    }),
    [hasAllPermissions, hasAnyPermission, hasPermission, effectiveIsLoading, effectiveIsReady, effectivePermissions],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

// --- Internal: Default fetcher using API client ---

type PermissionsProviderWithApiProps = {
  children: ReactNode;
  isAuthenticated: boolean;
};

function PermissionsProviderWithApi({ children, isAuthenticated }: PermissionsProviderWithApiProps): React.JSX.Element {
  const client = useApiClient('admin-auth');
  const api = useMemo(() => createPermissionsApi(client), [client]);
  const loadPermissions = useCallback((): Promise<string[]> => api.fetchMyPermissions(), [api]);

  return (
    <PermissionsProviderCore isAuthenticated={isAuthenticated} loadPermissions={loadPermissions}>
      {children}
    </PermissionsProviderCore>
  );
}

// --- Public Provider ---

export function PermissionsProvider({
  children,
  isAuthenticated = true,
  loadPermissions: loadPermissionsOverride,
}: PermissionsProviderProps): React.JSX.Element {
  if (loadPermissionsOverride) {
    return (
      <PermissionsProviderCore isAuthenticated={isAuthenticated} loadPermissions={loadPermissionsOverride}>
        {children}
      </PermissionsProviderCore>
    );
  }

  return <PermissionsProviderWithApi isAuthenticated={isAuthenticated}>{children}</PermissionsProviderWithApi>;
}

// --- Hook ---

export function usePermissions(): PermissionsContextValue {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a <PermissionsProvider>');
  }
  return context;
}
